/* eslint-disable*/
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import Swal from "sweetalert2";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const BulkCampaignList = () => {
  const [bulkCampaigns, setBulkCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");

  // 🔎 Search Filters
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [emailIdFilter, setEmailIdFilter] = useState("");
  const [smsIdFilter, setSmsIdFilter] = useState("");
  const [pushIdFilter, setPushIdFilter] = useState("");
  const [whatsappIdFilter, setWhatsappIdFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState(""); // ✅ New filter

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const navigate = useNavigate();

  const fetchBulkCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/templateBulkCampaign/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // Adjust based on actual API response structure
      setBulkCampaigns(response.data.templates || response.data.data || []);
    } catch (error) {
      setError(error.message);
      console.error("Error fetching bulk campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBulkCampaigns();
  }, []);

  // 🔎 Apply filters
  const filteredCampaigns = useMemo(() => {
    return bulkCampaigns.filter((campaign) => {
      if (
        templateNameFilter &&
        !(campaign.template_name || "")
          .toLowerCase()
          .includes(templateNameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        emailIdFilter &&
        !(campaign.mailTemplate?.toString() || "")
          .toLowerCase()
          .includes(emailIdFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        smsIdFilter &&
        !(campaign.smsTemplate?.toString() || "")
          .toLowerCase()
          .includes(smsIdFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        pushIdFilter &&
        !(campaign.pushTemplate?.toString() || "")
          .toLowerCase()
          .includes(pushIdFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        whatsappIdFilter &&
        !(campaign.whatsappTemplate?.toString() || "")
          .toLowerCase()
          .includes(whatsappIdFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        serviceFilter &&
        !(campaign.serviceName || "")
          .toLowerCase()
          .includes(serviceFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [
    bulkCampaigns,
    templateNameFilter,
    emailIdFilter,
    smsIdFilter,
    pushIdFilter,
    whatsappIdFilter,
    serviceFilter,
  ]);

  useEffect(() => {
    if (!bulkCampaigns.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [
    templateNameFilter,
    emailIdFilter,
    smsIdFilter,
    pushIdFilter,
    whatsappIdFilter,
    serviceFilter,
    bulkCampaigns,
  ]);



  // 📑 Update pagination when filters change
  useEffect(() => {
    setTotal(filteredCampaigns.length);
    setPageCount(Math.ceil(filteredCampaigns.length / limit));
    setPage(1);
  }, [filteredCampaigns, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedCampaigns = filteredCampaigns.slice(start - 1, end);

  // Delete Confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this bulk campaign!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${API_URL}settings/templateBulkCampaign/delete/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Remove the deleted item from state
        setBulkCampaigns((prev) =>
          prev.filter((campaign) => campaign.id !== id)
        );

        // Show message from server
        Swal.fire(
          "Deleted!",
          "Your bulk campaign has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting the bulk campaign.",
          "error"
        );
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleProcessCampaign = async (templateId) => {
    try {
      Swal.fire({
        title: "Processing Campaign...",
        text: "Please wait while we trigger the campaign.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await axios.post(
        `${API_URL}settings/templateBulkCampaign/process-campaign`,
        { templateId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data.message || "Bulk campaign processed successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to process bulk campaign. Please try again.",
      });
      console.error("Error processing campaign:", error);
    }
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTemplateNameFilter("");
      setEmailIdFilter("");
      setSmsIdFilter("");
      setPushIdFilter("");
      setWhatsappIdFilter("");
      setServiceFilter("");
    };
    window.addEventListener('clearTemplateBulkCampaignFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearTemplateBulkCampaignFilterEvent', handleClearFilters);
    };
  }, []);


  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Bulk Campaigns</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="settings_bulkcampaign" action="add">
              <Link
                to={"/Settings/TemplateBulkCampaign/add"}
                className="btn btn-default btn-sm"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Bulk Campaign
              </Link>
            </PermissionWrapper>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {bulkCampaigns.length > 0 && (
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div className="panel-heading border-0">
                <h4 className="panel-title m-0">Bulk Campaign Templates</h4>
              </div>
              <div className="d-inline-block">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}

          {loading || bulkCampaigns.length > 0 ? (
            <div className="table-responsive sticky-table-container">
              <style>
                {`
                    /* Sticky header rows */
                    .sticky-bulkcampaign-table thead tr:first-child th {
                       position: sticky;
                       top: 0;
                       z-index: 20;
                      background: #f8f9fa;
                     }

                    .sticky-bulkcampaign-table thead tr:nth-child(2) th {
                      position: sticky;
                      top: 30px; /* Adjust based on first header row height */
                      z-index: 19;
                     }

                    .sticky-table-container {
                       max-height: calc(100vh - 190px);
                      overflow: auto;
                    }

                     .sticky-bulkcampaign-table th {
                       vertical-align: top;
                      }
                  `}
              </style>
              <table className="table m-b-0 sticky-bulkcampaign-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Template Name</th>
                    <th>Email</th>
                    <th>SMS</th>
                    <th>Push</th>
                    <th>WhatsApp</th>
                    <th>Service Name</th> {/* ✅ New Column */}
                    <th>Action</th>
                  </tr>
                  {/* 🔎 Search Filters Row */}
                  <tr>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search name..."
                        value={templateNameFilter}
                        onChange={(e) => setTemplateNameFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search email..."
                        value={emailIdFilter}
                        onChange={(e) => setEmailIdFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search SMS..."
                        value={smsIdFilter}
                        onChange={(e) => setSmsIdFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search push..."
                        value={pushIdFilter}
                        onChange={(e) => setPushIdFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search WhatsApp..."
                        value={whatsappIdFilter}
                        onChange={(e) => setWhatsappIdFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search service..."
                        value={serviceFilter}
                        onChange={(e) => setServiceFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setTemplateNameFilter("");
                          setEmailIdFilter("");
                          setSmsIdFilter("");
                          setPushIdFilter("");
                          setWhatsappIdFilter("");
                          setServiceFilter("");
                        }}
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading || filterLoading ? (
                    Array.from({ length: 10 }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="skeleton-row">
                        {Array.from({ length: 8 }).map((_, colIndex) => (
                          <td key={colIndex}>
                            <div className="skeleton-box"></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : paginatedCampaigns.length > 0 ? (
                    paginatedCampaigns.map((campaign, index) => (
                      <tr key={campaign.id}>
                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td style={{ wordWrap: "break-word", whiteSpace: "normal" }}>
                          {campaign.template_name}
                        </td>
                        <td>{campaign.mailTemplate || ""}</td>
                        <td>{campaign.smsTemplate || ""}</td>
                        <td>{campaign.pushTemplate || ""}</td>
                        <td>{campaign.whatsappTemplate || ""}</td>
                        <td>{campaign.serviceName || ""}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <PermissionWrapper
                              module="settings_bulkcampaign"
                              action="edit"
                            >
                              <button
                                onClick={() =>
                                  navigate(
                                    `/Settings/TemplateBulkCampaign/edit/${campaign.id}`
                                  )
                                }
                                className="btn btn-warning btn-xs"
                                title="Edit"
                                disabled={campaign.isdelete}
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </button>
                            </PermissionWrapper>
                            <button
                              onClick={() => handleProcessCampaign(campaign.id)}
                              className="btn btn-info btn-xs"
                              title="Trigger Campaign Notification"
                            >
                              <FontAwesomeIcon icon={faBell} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" style={{ textAlign: "center" }}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-3">No bulk campaigns found.</div>
          )}
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default BulkCampaignList;
